import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { purchaseApprovalService, type ApprovalRule } from '@/services/purchaseApprovalService';

function formatINR(n: number) {
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
}

export default function ApprovalSettingsPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [rules, setRules] = useState<ApprovalRule[]>([]);
  const [loadingRules, setLoadingRules] = useState<boolean>(true);

  // Notification toggles
  const [emailNotifs, setEmailNotifs] = useState(true);
  const [notifyApproverNew, setNotifyApproverNew] = useState(true);
  const [notifyCreatorStatus, setNotifyCreatorStatus] = useState(true);
  const [notifyTimeout, setNotifyTimeout] = useState(true);
  const [ccEmails, setCcEmails] = useState('');

  // Escalation toggles
  const [autoEscalate, setAutoEscalate] = useState(true);
  const [escalationEmail, setEscalationEmail] = useState(true);

  useEffect(() => {
    async function fetchRules() {
      try {
        const data = await purchaseApprovalService.getRules();
        setRules(data);
      } catch (err) {
        console.warn('Failed to load rules:', err);
      } finally {
        setLoadingRules(false);
      }
    }
    fetchRules();
  }, []);

  const updateRule = (id: string, patch: Partial<ApprovalRule>) => {
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const toggleActive = (id: string) => {
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, isActive: !r.isActive } : r)));
  };

  const [isSavingRules, setIsSavingRules] = useState(false);

  const deleteRule = (id: string) => {
    setRules((prev) => prev.filter((r) => r.id !== id));
    showToast('Rule removed from list. Click "Save Rules" to apply changes.', 'info');
  };

  const addRule = () => {
    const newRule: ApprovalRule = {
      id: `rule-${Date.now()}`,
      name: 'New Approval Rule',
      minAmount: 0,
      maxAmount: null,
      approverRole: 'PURCHASE_MANAGER',
      approverName: 'Purchase Manager',
      approverId: '',
      timeoutHours: 24,
      escalateTo: 'Director',
      isActive: true,
    };
    setRules((prev) => [...prev, newRule]);
    showToast('New rule added. Edit and click "Save Rules".', 'info');
  };

  const saveRules = async () => {
    setIsSavingRules(true);
    try {
      await purchaseApprovalService.saveRules(rules);
      showToast('Approval rules saved to database successfully', 'success');
      const refreshed = await purchaseApprovalService.getRules();
      setRules(refreshed);
    } catch (err: any) {
      showToast(err.message || 'Failed to save rules', 'error');
    } finally {
      setIsSavingRules(false);
    }
  };

  const saveSettings = () => {
    showToast('Notification & escalation settings saved', 'success');
  };

  const testEmail = () => {
    showToast('Test email sent (mock)', 'success');
  };

  const inpClass = 'w-full h-8 px-2 text-xs border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]';
  const lbClass = 'block text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1';

  return (
    <AppLayout>
      <div className="p-6 bg-[#f8fafc] min-h-full space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs text-slate-400 mb-0.5">
              <button onClick={() => navigate('/purchase/approvals')} className="hover:text-[#4f46e5] cursor-pointer">PO Approvals</button>
              <i className="ri-arrow-right-s-line" />
              <span className="text-[#4f46e5] font-semibold">Settings</span>
            </div>
            <h1 className="text-xl font-bold text-[#1e293b]">PO Approval Settings</h1>
            <p className="text-sm text-slate-500 mt-0.5">Configure approval rules and notifications</p>
          </div>
        </div>

        {/* ─── SECTION 1 — APPROVAL RULES TABLE ─── */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#e2e8f0] flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#1e293b]">Value-based Approval Rules</h3>
            <span className="text-xs text-slate-400">{rules.length} rules configured</span>
          </div>

          {/* Info box */}
          <div className="mx-5 mt-4 bg-sky-50 border border-sky-200 rounded-lg p-3 flex items-start gap-3">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-sky-100 shrink-0">
              <i className="ri-information-line text-sky-600 text-sm" />
            </div>
            <div className="text-xs text-sky-700 leading-relaxed">
              <strong>Rules are checked in order of minimum amount.</strong><br />
              POs below ₹10,000 are auto-approved. POs above ₹5,00,000 require MD approval with no timeout.
            </div>
          </div>

          <div className="overflow-x-auto mt-3">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  {['Rule Name', 'Min Amount (₹)', 'Max Amount (₹)', 'Approver', 'Timeout (hrs)', 'Escalate To', 'Active', ''].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-left font-semibold text-slate-500 border-b border-[#e2e8f0] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id} className="border-b border-slate-50 hover:bg-indigo-50/20 transition-colors">
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={rule.name}
                        onChange={(e) => updateRule(rule.id, { name: e.target.value })}
                        className={inpClass}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={rule.minAmount}
                        onChange={(e) => updateRule(rule.id, { minAmount: Number(e.target.value) })}
                        className={`${inpClass} w-24`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={rule.maxAmount ?? ''}
                        placeholder="No limit"
                        onChange={(e) => updateRule(rule.id, { maxAmount: e.target.value ? Number(e.target.value) : null })}
                        className={`${inpClass} w-24`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      {rule.approverRole === 'AUTO' ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">Auto</span>
                      ) : (
                        <input
                          type="text"
                          value={rule.approverName}
                          onChange={(e) => updateRule(rule.id, { approverName: e.target.value })}
                          className={inpClass}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        value={rule.timeoutHours}
                        onChange={(e) => updateRule(rule.id, { timeoutHours: Number(e.target.value) })}
                        className={`${inpClass} w-16`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={rule.escalateTo ?? ''}
                        placeholder="—"
                        onChange={(e) => updateRule(rule.id, { escalateTo: e.target.value || null })}
                        className={`${inpClass} w-28`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => toggleActive(rule.id)}
                        className={`text-[10px] px-2 py-0.5 rounded-full border font-medium cursor-pointer whitespace-nowrap transition-colors ${rule.isActive ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-400 border-slate-200'}`}
                      >
                        {rule.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => deleteRule(rule.id)}
                        className="w-6 h-6 flex items-center justify-center text-slate-400 hover:text-red-500 rounded cursor-pointer transition-colors"
                        title="Remove Rule"
                      >
                        <i className="ri-delete-bin-line text-xs" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-3 border-t border-[#e2e8f0] flex items-center justify-between">
            <button onClick={addRule} className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-dashed border-[#a5b4fc] text-xs font-medium text-[#4f46e5] hover:bg-indigo-50 active:scale-95 cursor-pointer whitespace-nowrap transition-all">
              <i className="ri-add-line" />Add Rule
            </button>
            <button onClick={saveRules} disabled={isSavingRules} className="flex items-center gap-1.5 h-8 px-4 rounded-lg bg-[#4f46e5] text-xs font-medium text-white hover:bg-indigo-700 active:scale-95 disabled:opacity-50 cursor-pointer whitespace-nowrap transition-all">
              <i className={isSavingRules ? "ri-loader-4-line animate-spin" : "ri-save-line"} />
              {isSavingRules ? 'Saving...' : 'Save Rules'}
            </button>
          </div>
        </div>

        {/* ─── SECTION 2 — NOTIFICATION SETTINGS ─── */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#e2e8f0]">
            <h3 className="text-sm font-semibold text-[#1e293b]">Notification Settings</h3>
          </div>
          <div className="p-5 space-y-4">
            {/* Master toggle */}
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-[#1e293b]">Email Notifications</p>
                <p className="text-xs text-slate-400">Enable email alerts for approval events</p>
              </div>
              <button
                onClick={() => setEmailNotifs(!emailNotifs)}
                className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${emailNotifs ? 'bg-emerald-500' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow ${emailNotifs ? 'translate-x-5' : ''}`} />
              </button>
            </div>

            <div className="border-t border-[#f1f5f9] pt-4 space-y-3">
              <div className="flex items-center justify-between py-1.5">
                <div>
                  <p className="text-sm text-[#1e293b]">Notify approver when</p>
                  <p className="text-xs text-slate-400">New PO submitted for approval</p>
                </div>
                <button
                  onClick={() => setNotifyApproverNew(!notifyApproverNew)}
                  className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${notifyApproverNew ? 'bg-emerald-500' : 'bg-slate-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow ${notifyApproverNew ? 'translate-x-5' : ''}`} />
                </button>
              </div>

              <div className="flex items-center justify-between py-1.5">
                <div>
                  <p className="text-sm text-[#1e293b]">Notify creator when</p>
                  <p className="text-xs text-slate-400">PO approved / rejected / sent back</p>
                </div>
                <button
                  onClick={() => setNotifyCreatorStatus(!notifyCreatorStatus)}
                  className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${notifyCreatorStatus ? 'bg-emerald-500' : 'bg-slate-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow ${notifyCreatorStatus ? 'translate-x-5' : ''}`} />
                </button>
              </div>

              <div className="flex items-center justify-between py-1.5">
                <div>
                  <p className="text-sm text-[#1e293b]">Notify on timeout</p>
                  <p className="text-xs text-slate-400">Alert when approval is overdue</p>
                </div>
                <button
                  onClick={() => setNotifyTimeout(!notifyTimeout)}
                  className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${notifyTimeout ? 'bg-emerald-500' : 'bg-slate-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow ${notifyTimeout ? 'translate-x-5' : ''}`} />
                </button>
              </div>
            </div>

            {/* Email recipients */}
            <div className="border-t border-[#f1f5f9] pt-4 space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={lbClass}>Creator Email</label>
                  <input type="email" value="admin@invenpro.com" disabled className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg bg-slate-50 text-slate-400" />
                </div>
                <div>
                  <label className={lbClass}>CC (comma separated)</label>
                  <input
                    type="text"
                    value={ccEmails}
                    onChange={(e) => setCcEmails(e.target.value)}
                    placeholder="manager@company.com, director@company.com"
                    className="w-full h-9 px-3 text-sm border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
                  />
                </div>
              </div>
              <button onClick={testEmail} className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[#e2e8f0] text-xs font-medium text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap transition-colors">
                <i className="ri-mail-send-line" />Test Email
              </button>
            </div>
          </div>
        </div>

        {/* ─── SECTION 3 — ESCALATION SETTINGS ─── */}
        <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#e2e8f0]">
            <h3 className="text-sm font-semibold text-[#1e293b]">Escalation Settings</h3>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-[#1e293b]">Auto-escalate on timeout</p>
                <p className="text-xs text-slate-400">Automatically escalate to next level when approver exceeds timeout</p>
              </div>
              <button
                onClick={() => setAutoEscalate(!autoEscalate)}
                className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${autoEscalate ? 'bg-emerald-500' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow ${autoEscalate ? 'translate-x-5' : ''}`} />
              </button>
            </div>

            {/* Escalation chain */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-xs font-semibold text-amber-800 mb-2">Escalation Chain</p>
              <div className="flex items-center gap-2 text-xs text-amber-700">
                <span className="px-2 py-1 bg-amber-100 rounded font-medium">Purchase Manager</span>
                <i className="ri-arrow-right-line text-amber-400" />
                <span className="px-2 py-1 bg-amber-100 rounded font-medium">Director</span>
                <i className="ri-arrow-right-line text-amber-400" />
                <span className="px-2 py-1 bg-amber-100 rounded font-medium">MD</span>
              </div>
              <p className="text-xs text-amber-600 mt-2">
                If Purchase Manager doesn't respond in 48 hrs, escalates to Director
              </p>
            </div>

            <div className="flex items-center justify-between py-1.5">
              <div>
                <p className="text-sm text-[#1e293b]">Escalation email notification</p>
                <p className="text-xs text-slate-400">Send email when a PO is escalated</p>
              </div>
              <button
                onClick={() => setEscalationEmail(!escalationEmail)}
                className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${escalationEmail ? 'bg-emerald-500' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow ${escalationEmail ? 'translate-x-5' : ''}`} />
              </button>
            </div>
          </div>

          <div className="px-5 py-3 border-t border-[#e2e8f0] flex justify-end">
            <button onClick={saveSettings} className="flex items-center gap-1.5 h-9 px-5 rounded-lg bg-[#4f46e5] text-sm font-medium text-white hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors">
              <i className="ri-save-line" />Save Settings
            </button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}